import { useRef, useEffect, FC, useMemo } from 'react';
import { useTranslate } from 'hooks/useTranslate';
import { AI_NAME } from 'modules/ai/constants/aiName';

import FloatingComposer from 'modules/ai/components/FloatingComposer';
import { Message } from '../types/Message';

import { useSocietyAiAgentPage } from '../context/SocietyAiAgentPageContext';

import NaviOrb from './NaviOrb';
import UserMessage from './UserMessage';
import AssistantMessage from './AssistantMessage';

const findPreviousUserQuestion = (
  messages: Message[],
  assistantIndex: number
): string => {
  for (let i = assistantIndex - 1; i >= 0; i -= 1) {
    if (messages[i]?.role === 'user') {
      return messages[i].content;
    }
  }
  return '';
};

const Conversation: FC = () => {
  const { t } = useTranslate();
  const { data, actions } = useSocietyAiAgentPage();

  const scrollRef = useRef<HTMLDivElement>(null);

  const messagesWithContext = useMemo(
    () =>
      data.messages.map((m, index) => ({
        message: m,
        userQuestion:
          m.role === 'assistant'
            ? findPreviousUserQuestion(data.messages, index)
            : '',
      })),
    [data.messages]
  );

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [data.messages, data.isThinking, data.streamingText]);

  return (
    <div className="navi-conversation">
      <header className="navi-conversation__header">
        <div className="navi-conversation__identity">
          <NaviOrb size={28} showSparkles={false} />
          <div>
            <p className="navi-conversation__name">
              {AI_NAME}
              <span className="navi-conversation__name-suffix">- Sttok AI</span>
            </p>
            <div className="navi-conversation__status">
              <span
                className={`navi-conversation__status-dot${
                  data.isThinking
                    ? ' navi-conversation__status-dot--thinking'
                    : ''
                }`}
              />
              {data.isThinking ? t('CHATBOT_Thinking') : t('CHATBOT_Online')}
            </div>
          </div>
        </div>
        <button
          type="button"
          className="navi-conversation__reset"
          onClick={actions.handleResetConversation}
        >
          {t('CHATBOT_NewChat')}
        </button>
      </header>
      <div ref={scrollRef} className="navi-conversation__messages">
        {messagesWithContext.map(({ message: m, userQuestion }) =>
          m.role === 'user' ? (
            <UserMessage key={m.id} content={m.content} />
          ) : (
            <AssistantMessage
              key={m.id}
              messageId={m.id}
              content={m.content}
              userQuestion={userQuestion}
              conversationId={data.activeConversationId}
              feedback={m.feedback}
            />
          )
        )}
        {data.streamingText ? (
          <AssistantMessage content={data.streamingText} streaming />
        ) : (
          data.isThinking && <AssistantMessage thinking />
        )}
      </div>
      <footer className="navi-conversation__footer">
        <FloatingComposer placeholder="CHATBOT_ReplyTo" />
        <p className="navi-conversation__disclaimer">
          {t('CHATBOT_VerifyMessage')}
        </p>
      </footer>
    </div>
  );
};

export default Conversation;
